import React, { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FeatureKey, setFeatureEnabled } from '../../api/featuresApi';
import { useFeatures } from '../../hooks/useFeatures';
import { useIsSuperadmin } from '../../hooks/useIsAdmin';

const TOGGLES: Array<{ key: Exclude<FeatureKey, 'aiAssistant' | 'services'>; label: string; description: string }> = [
    { key: 'parking', label: 'Parkering', description: 'Parkeringsområden, platser och parkeringskö.' },
    { key: 'storageUnits', label: 'Förråd', description: 'Förråd i byggnaderna.' },
    { key: 'units', label: 'Lägenheter & lokaler', description: 'Lägenheter och lokaler i byggnaderna.' },
    { key: 'keyRegister', label: 'Nycklar', description: 'Nyckelregistret med nycklar och taggar.' },
];

/**
 * Inställningar → Funktioner: admins hide the features their organisation does not use.
 * Turning one off only hides it; nothing is deleted. Hyresgäster and hyresavtal follow
 * automatically (on while any of these is on), so they have no switch. Service is activated per
 * customer, so only superadmins see its switch.
 */
const FeaturesSettings: React.FC = () => {
    const features = useFeatures();
    const isSuperadmin = useIsSuperadmin();
    const [saving, setSaving] = useState<FeatureKey | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleToggle = async (key: FeatureKey, next: boolean) => {
        setError(null);
        setSaving(key);
        try {
            await setFeatureEnabled(key, next);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara inställningen');
        } finally {
            setSaving(null);
        }
    };

    // Disabled until the toggles have been read, and while one is being saved.
    const renderSwitch = (key: FeatureKey, label: string) => (
        <Form.Check
            type="switch"
            id={`feature-${key}`}
            label={<strong>{label}</strong>}
            checked={features?.[key] ?? false}
            disabled={!features || saving !== null}
            onChange={(e) => handleToggle(key, e.target.checked)}
        />
    );

    return (
        <div style={{ maxWidth: 640 }}>
            <p className="text-muted">
                Stäng av de delar av appen som er organisation inte använder. En avstängd funktion döljs
                för alla användare på webben och i appen; inget tas bort och allt syns igen när den slås på.
            </p>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {TOGGLES.map(({ key, label, description }) => (
                <div key={key} className="mb-3">
                    {renderSwitch(key, label)}
                    <div className="text-muted small ms-5">{description}</div>
                </div>
            ))}

            <div className="mb-3">
                {isSuperadmin ? (
                    renderSwitch('aiAssistant', 'AI-assistent')
                ) : (
                    <div className="d-flex align-items-center gap-2">
                        <strong>AI-assistent</strong>
                        <span className="badge bg-secondary">Kommande funktion</span>
                    </div>
                )}
                <div className={`text-muted small${isSuperadmin ? ' ms-5' : ''}`}>
                    Byggnadsassistenten svarar på frågor om fastighetsbeståndet.
                    {isSuperadmin && ' Den visas bara för superadministratörer.'}
                </div>
            </div>

            {isSuperadmin && (
                <div className="mb-3">
                    {renderSwitch('services', 'Service')}
                    <div className="text-muted small ms-5">
                        Service av toalettbyggnader: fliken Service i appen och Analys på webben.
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeaturesSettings;
